import React, { useState, useEffect } from 'react';
import { useConsent } from '../hooks/useConsent';
import { COOKIE_CATEGORIES } from '../data/cookiePolicy';
import { CookieCategory, CookieConsentPreferences } from '@/types';
import { Link } from 'react-router-dom';
import { useShellI18n } from '@/features/language/useI18n';
import styles from './CookieSettings.module.css';

export const CookieSettings: React.FC = () => {
  const { isSettingsOpen, closeSettings, preferences, savePreferences, acceptAll, rejectAll } =
    useConsent();
  const [localPreferences, setLocalPreferences] = useState<CookieConsentPreferences>(preferences);
  const { t } = useShellI18n();

  // Sync local state when store preferences change or modal opens
  useEffect(() => {
    setLocalPreferences(preferences);
  }, [preferences, isSettingsOpen]);

  if (!isSettingsOpen) return null;

  const handleToggle = (categoryId: CookieCategory) => {
    if (categoryId === 'necessary') return; // Cannot toggle necessary

    setLocalPreferences((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const handleSave = () => {
    savePreferences(localPreferences);
  };

  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="settings-title"
    >
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2 id="settings-title" className={styles.title}>
            {t('Cookie Preferences')}
          </h2>
          <button onClick={closeSettings} className={styles.closeButton} aria-label={t('Close')}>
            &times;
          </button>
        </div>

        <div className={styles.body}>
          <p className={styles.description}>
            {t(
              'We use necessary technologies for secure authentication and Portal operation. Optional preference storage remembers interface settings. Details are listed below.'
            )}
            <br />
            {t(
              'Necessary technologies are always active because the requested Portal service cannot be provided securely without them.'
            )}
            <br />
            <Link to="/privacy-policy" onClick={closeSettings}>
              {t('Read our Privacy Policy')}
            </Link>
          </p>

          <div className={styles.actions}>
            <button onClick={acceptAll} className={styles.buttonSecondary}>
              {t('Allow All')}
            </button>
            <button onClick={rejectAll} className={styles.buttonSecondary}>
              {t('Reject All')}
            </button>
          </div>

          <div className={styles.categories}>
            {COOKIE_CATEGORIES.map((category) => (
              <div key={category.id} className={styles.categoryItem}>
                <div className={styles.categoryHeader}>
                  <div className={styles.categoryTitleWrapper}>
                    <h3 className={styles.categoryTitle}>{t(category.name)}</h3>
                    <span
                      className={category.required ? styles.badgeRequired : styles.badgeOptional}
                    >
                      {category.required ? t('Always Active') : t('(Optional)')}
                    </span>
                  </div>
                  {!category.required && (
                    <label className={styles.switch}>
                      <input
                        type="checkbox"
                        aria-label={t(category.name)}
                        checked={localPreferences[category.id] || false}
                        onChange={() => handleToggle(category.id)}
                      />
                      <span className={styles.slider}></span>
                    </label>
                  )}
                </div>
                <p className={styles.categoryDescription}>{t(category.description)}</p>
                <details className={styles.cookieDetails}>
                  <summary>{t('View Cookies')}</summary>
                  <table className={styles.cookieTable}>
                    <thead>
                      <tr>
                        <th>{t('Name')}</th>
                        <th>{t('Provider')}</th>
                        <th>{t('Purpose')}</th>
                        <th>{t('Expiry')}</th>
                        <th>{t('Type')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {category.cookies.map((cookie) => (
                        <tr key={cookie.name}>
                          <td>{cookie.name}</td>
                          <td>{t(cookie.provider)}</td>
                          <td>{t(cookie.purpose)}</td>
                          <td>{t(cookie.expiry)}</td>
                          <td>{t(cookie.type)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.footer}>
          <button onClick={handleSave} className={styles.buttonPrimary}>
            {t('Save Preferences')}
          </button>
        </div>
      </div>
    </div>
  );
};
